import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { classifyProspectLoad } from "../lib/prospectLoad";

// The three existing reads behind the quick panel (no new endpoint): the prospect, its visible tasks (only with `tasks:view`) and its
// feed of exchanges. The prospect decides the state (loading / ready / unavailable = 403 or 404 / error); the two others fail alone:
// `null` = not available to this person (403/404 or not asked), `"error"` = worth a retry. A slow answer for a previous pharmacy
// never lands on the current one.
export default function useQuickPanelData(id, { withTasks, version = 0 }) {
  const [state, setState] = useState({ status: "loading", prospect: null, tasks: null, feed: null });
  const seq = useRef(0);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!id) return undefined;
    const mine = ++seq.current;
    const alive = () => mine === seq.current;
    setState((s) => (s.prospect?.id === id ? s : { status: "loading", prospect: null, tasks: null, feed: null }));
    const optional = (promise) => promise.then(({ data }) => data).catch((e) => (classifyProspectLoad(e) === "error" ? "error" : null));
    Promise.all([
      api.get(`/prospects/${id}`).then(({ data }) => ({ data })).catch((error) => ({ error })),
      withTasks ? optional(api.get("/tasks", { params: { prospect_id: id } })) : Promise.resolve(null),
      optional(api.get(`/prospects/${id}/activity`)),
    ]).then(([prospect, tasks, feed]) => {
      if (!alive()) return;
      if (prospect.error) setState({ status: classifyProspectLoad(prospect.error) === "error" ? "error" : "unavailable", prospect: null, tasks: null, feed: null });
      else setState({ status: "ready", prospect: prospect.data, tasks, feed });
    });
    return () => { seq.current += 1; };
  }, [id, withTasks, version, retry]);

  const reload = useCallback(() => setRetry((n) => n + 1), []);
  return { ...state, reload };
}
